import { data, isRouteErrorResponse, useLoaderData, useNavigation, useParams, useRouteError } from 'react-router';
import { DateCell, ErrorAlert, JsonDetails } from '../components/ui.jsx';
import RelationList from '../components/RelationList.jsx';
import RelatedCompanies from '../components/RelatedCompanies.jsx';
import { RELATION_LIMIT } from '../data/db.js';
import { loadPerson } from '../data/person.js';
import { currentAddress } from '../data/entity.js';
import { notFoundMeta, pageMeta } from '../seo.js';

// The server gets the person and the companies before it sends the page, so the HTML
// holds them for Google (data/person.js).
export async function loader({ params }) {
  const loaded = await loadPerson(params.id);
  if (!loaded) throw data(null, { status: 404 });
  return loaded;
}

export function meta({ data: loaded, error, params }) {
  if (error || !loaded) return notFoundMeta();
  const { person, list, count } = loaded;
  const name = person?.name ?? `Enhed ${params.id}`;
  const address = currentAddress(person?.beliggenhedsadresse);
  const names = [...list.active, ...list.inactive].slice(0, 5).map((item) => item.name).filter(Boolean);
  const parts = [name];
  if (address) parts.push(address);
  parts.push(`Takes part in ${count} ${count === 1 ? 'company' : 'companies'}${names.length ? `: ${names.join(', ')}` : ''}`);
  return pageMeta({
    title: `${name} – person in the CVR register`,
    description: `${parts.join('. ')}.`,
    path: `/Person/${params.id}`,
  });
}

export function ErrorBoundary() {
  const error = useRouteError();
  const { id } = useParams();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <div className="alert error">{`No person or participant has the number ${id}.`}</div>;
  }
  return <ErrorAlert error={error} />;
}

export default function PersonPage() {
  const { id } = useParams();
  const { person: p, list, count } = useLoaderData();
  const loading = useNavigation().state === 'loading';

  return (
    <div className={loading ? 'is-loading' : undefined}>
      <header className="entity-head">
        <div className="eyebrow">Person · Enhedsnummer {id}</div>
        <h1>{p?.name ?? `Enhed ${id}`}</h1>
        {!p && <div className="alert note">The collection cvrperson has no person with this number.</div>}
        {p && (
          <dl className="facts">
            <div><dt>Address</dt><dd>{currentAddress(p.beliggenhedsadresse) || '—'}</dd></div>
            <div><dt>Opdateret</dt><dd><DateCell value={p.sidstOpdateret} /></dd></div>
            <div><dt>Companies</dt><dd>{count}</dd></div>
          </dl>
        )}
      </header>

      <section className="card">
        <div className="card-head"><h2>Companies</h2></div>
        <div className="card-body">
          <RelationList active={list.active} inactive={list.inactive} empty="This person takes part in no company." />
          {count === RELATION_LIMIT && <p className="muted">The list stops at {RELATION_LIMIT} companies.</p>}
        </div>
      </section>

      <RelatedCompanies enhedsNummer={Number(id)} />
      {p && <JsonDetails value={p} />}
    </div>
  );
}
